import React from 'react';
import styled, { css } from 'styled-components';
import { Col, Row, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import chroma from 'chroma-js';
import Card from '../Card';
import { darkRed, green } from '../../styles/constants';
import dateMappings from '../../utils/dateParts';
import { selectBeesById } from '../../ducks/bees';
import { selectCoverageGoal } from '../../ducks/floweringArch';
import TableScrollContainer from '../TableScrollContainer';
import { getRelevantMonthsFromArch } from './utils';
import GuidedTour from '../../containers/GuidedTour';
import sortBeesByDutchName from "../../utils/sortBeesByDutchName";

const groupWithoutOffset = css`
  td:nth-child(odd) {
    padding: 0.75rem 0.75rem 0.75rem 0.1rem;
  }
  
   td:nth-child(even) {
    padding: 0.75rem  0.1rem 0.75rem 0.75rem;
  }
`;

const groupWithOffset = css`
  td:nth-child(even) {
    padding: 0.75rem 0.75rem 0.75rem 0.1rem;
  }

   td:nth-child(odd) {
    padding: 0.75rem  0.1rem 0.75rem 0.75rem;
  }
`;

const StyledTable = styled(Table).withConfig({
  shouldForwardProp: (prop) => !['groupShouldOffset'].includes(prop),
})`
  text-align: center;
  font-size: 0.9rem;
  margin-bottom: 0;

  // No border styles
  td, th {
      border: 0 !important;
      background-clip: content-box;
  }
  
  ${(props) => (props.groupShouldOffset ? groupWithOffset : groupWithoutOffset)};
  
  // Decrease padding left on first column
  th:first-of-type, td:first-of-type {
    padding: 0.75rem  0.75rem  0.75rem 0;
  }
  
  // Bold bee names
  td:first-of-type {
    font-weight: 600;
  }
  
  tbody tr td:first-of-type {
    text-align: start;
    padding-left: 0.75rem;
    white-space: nowrap;
    position: sticky;
  }
`;

const MonthHeader = styled.tr`
  border-bottom: 1px solid #dee2e6;
  
    ${({ showBeforeMonth }) => showBeforeMonth && `
      th:nth-of-type(3){
        font-weight: normal !important;
        font-size: 0.8em;
        opacity: 0.6;
      }
    `}
  
  ${({ showAfterMonth }) => showAfterMonth && `
    th:last-of-type{
    font-weight: normal !important;
    font-size: 0.8em;
    opacity: 0.6;
    }
  `}
`;

const DekkingHeader = styled.tr`
  th {
    font-weight: normal;
  }

  th:nth-of-type(2) {
    font-weight: 600 !important;
  }
`;

const Td = styled.td`
  width: 50px;
  background-color: ${(props) => props.color || 'transparent'};
  color: rgba(255,255,255,0.7);
  font-weight: 600;
  font-size: 0.8rem;
`;

const Bold = styled.span`
  font-weight: 600;
`;

const FloweringArch = ({ archInfo, showTour }) => {
  const {
    arch,
    coverage,
    minMonth,
    maxMonth,
  } = archInfo;

  const coverageGoal = useSelector(selectCoverageGoal);
  const beesById = useSelector(selectBeesById);

  let isFirstFlightMonth = true;
  let isFirstBee = true;

  if (!arch) {
    return null;
  }

  const tdColors = chroma.scale([darkRed, green])
    .mode('lch').colors(coverageGoal + 1); // +1 because we also need a color for 0 interactible plants

  const getTdColor = (amountInteractibleBees) => {
    // Because the tdColors array doesn't extend to infinity we take the interactibleGoal as the
    // max value.
    if (amountInteractibleBees > coverageGoal) {
      amountInteractibleBees = coverageGoal;
    }

    return tdColors[amountInteractibleBees];
  };

  const beeIds = Object.keys(arch);
  const sortedBeeIds = sortBeesByDutchName(beeIds, beesById)

  const relevantMonths = getRelevantMonthsFromArch(archInfo);

  const showBeforeMonth = dateMappings[minMonth]?.index >= 2;
  const showAfterMonth = dateMappings[maxMonth]?.index <= 21;

  const renderRelevantMonths = (beeId) => relevantMonths.map(
    (relevantMonth) => {
      const amountInteractibleBees = arch[beeId][relevantMonth];
      if (amountInteractibleBees === undefined) {
        return <Td key={relevantMonth} color={null} />;
      }

      const correctedAmount = amountInteractibleBees || 0;

      const id = isFirstFlightMonth && 'flowering-arch-flight-month';
      isFirstFlightMonth = false;

      return (
        <Td key={relevantMonth} color={getTdColor(amountInteractibleBees)} id={id || undefined}>{correctedAmount}</Td>
      );
    },
  );

  const renderRelevantCoverage = () => relevantMonths.map((relevantMonth) => (
    <th key={relevantMonth}>
      {coverage.perDatepart[relevantMonth]}
      %
    </th>
  ));

  return (
    <>
      {
        showTour && (<GuidedTour />)
      }
      <Row>
        <Col>
          <Card className="pb-0" id="flowering-arch">
            <Row>
              <Col>
                <h5>Bloeiboog</h5>
                <p className="mb-0">
                  <Bold>Doelstelling: </Bold>
                  {' '}
                  {coverageGoal}
                  {' '}
                  voedselplanten per bijensoort
                </p>
                <p>
                  <Bold>Gemiddelde voedseldekking: </Bold>
                  {coverage.overall}
                  %
                </p>
              </Col>
            </Row>
            <Row>
              <Col className="p-0">
                <TableScrollContainer>
                  <StyledTable
                    striped
                    groupShouldOffset={showBeforeMonth}
                  >
                    <thead>
                      <MonthHeader
                        showBeforeMonth={showBeforeMonth}
                        showAfterMonth={showAfterMonth}
                      >
                        <th />
                        <th />
                        {showBeforeMonth && <th>[...]</th>}
                        {
                        relevantMonths
                          .map(
                            (relevantMonth) => (dateMappings[relevantMonth].isFirstOfMonth
                              ? <th key={relevantMonth}>{dateMappings[relevantMonth].month}</th>
                              : <th key={relevantMonth} />),
                          )
                      }
                        {showAfterMonth && <th>[...]</th>}
                      </MonthHeader>
                      <DekkingHeader id="flowering-arch-food-coverage">
                        <th />
                        <th>Dekking</th>
                        {showBeforeMonth && <th />}
                        {renderRelevantCoverage()}
                        {showAfterMonth && <th />}
                      </DekkingHeader>
                    </thead>
                    <tbody>
                      {sortedBeeIds.map((beeId) => {
                        const id = isFirstBee && 'flowering-arch-bee-row';
                        isFirstBee = false;

                        return (
                          <tr key={beeId} id={id}>
                            <td>
                              {beesById[beeId].dutchName}
                            </td>
                            <td>
                              {coverage.perBee[beeId]}
                              %
                            </td>
                            {showBeforeMonth && <td />}
                            {renderRelevantMonths(beeId)}
                            {showAfterMonth && <td />}
                          </tr>
                        );
                      })}
                    </tbody>
                  </StyledTable>
                </TableScrollContainer>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </>
  );
};

FloweringArch.propTypes = {
  showTour: PropTypes.bool,
  archInfo: PropTypes.shape({
    arch: PropTypes.shape(),
    coverage: PropTypes.shape(),
    minMonth: PropTypes.string,
    maxMonth: PropTypes.string,
  }),
};

FloweringArch.defaultProps = {
  showTour: false,
  archInfo: null,
};

export default FloweringArch;
